"use client";

/**
 * WalletCardsSection — explicit per-wallet view in /portfolio.
 *
 * The user owns up to 4 distinct addresses across two chains, with very
 * different roles. The default portfolio header rolls everything up
 * into one balance, which hides where the funds actually live (deposit
 * wallet vs. legacy Safe vs. Solana ATA) and confuses users who look
 * for a specific address to top up or withdraw from.
 *
 * Render order:
 *   1. Polymarket Trading — `deposit_wallet_address` if user migrated
 *      (`wallet_type === 'deposit'`), otherwise the Safe row stands in
 *      for primary trading.
 *   2. Solana / Kalshi    — `solana_address`, USDC SPL + SOL gas.
 *   3. Polymarket Legacy  — `safe_address` only when it's not the
 *      primary AND it still holds residual USDC.e/pUSD or has open
 *      positions. Hidden when fully drained.
 *   4. Login Wallet       — `wallet_address` (Privy EOA), collapsed.
 *      Signing-only; no balance fetch — surfaces just the address with
 *      a copy button so users can identify which account they're on.
 */
import { useMemo, useState } from "react";
import {
  FiCopy,
  FiCheck,
  FiExternalLink,
  FiAlertCircle,
} from "react-icons/fi";
import { useUserMeQuery } from "@/hooks/useUserMeQuery";
import { usePolymarketBalanceContext } from "@/contexts/PolymarketBalanceContext";
import { useSolanaUSDCBalance } from "@/hooks/useSolanaUSDCBalance";
import { useHyperliquidBalance } from "@/hooks/useHyperliquidBalance";
import { useExternalWalletBalance } from "@/hooks/useExternalWalletBalance";
import { formatCurrency } from "@/lib/format";
import { useT } from "@/hooks/useT";
import NetworkWarningBadge from "@/app/components/NetworkWarningBadge";

type WalletKind = "deposit" | "solana" | "safe-legacy" | "evm-login" | "hyperliquid";
type ChainKind = "polygon" | "solana" | "ethereum" | "arbitrum";
type SourceKind = "polymarket" | "kalshi" | "hyperliquid" | null;

interface WalletCardData {
  kind: WalletKind;
  /** "Deposit Wallet", "Solana Wallet", "EVM Wallet" — what the user calls it. */
  label: string;
  /** Optional second-line context: "Polymarket trading", "Kalshi collateral". */
  sublabel?: string;
  badge?: string;
  badgeTone?: "active" | "legacy" | "auth";
  chain: ChainKind;
  /** Platform that consumes this wallet (rendered as a small badge over
   *  the chain logo). null → no overlay (e.g. login wallet has no source). */
  source: SourceKind;
  address: string;
  explorerUrl: string;
  primaryBalance: number | null; // USDC value
  primaryBalanceLabel?: string;
  primaryLoading?: boolean;
  gasBalance?: { amount: number; symbol: string } | null;
  positionsValue?: number;
  positionsCount?: number;
  positionsLabel?: string;
  /** Render this card collapsed (login-wallet style — no balance row). */
  collapsed?: boolean;
  /** Soft warning shown beneath the row, e.g. legacy wallet with funds. */
  warning?: string | null;
  /** Polygon-only custody address (deposit wallet / legacy Safe). Renders the
   *  shared network warning under the card: this page is the other place users
   *  copy that address from, and a send on the wrong network strands the funds
   *  (3 users, $88.92 on Ethereum mainnet — audit 2026-08-24). Solana/EOA cards
   *  leave it off. */
  polygonOnly?: boolean;
}

function truncate(addr: string): string {
  return addr.length >= 10 ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : addr;
}

function ChainGlyph({ chain }: { chain: ChainKind }) {
  if (chain === "polygon") {
    return (
      <div className="w-10 h-10 rounded-[5px] bg-[#8247E5]/10 flex items-center justify-center flex-shrink-0">
        <svg viewBox="0 0 38 33" className="w-5 h-5" fill="#8247E5" xmlns="http://www.w3.org/2000/svg">
          <path d="M29 11.6c-.7-.4-1.6-.4-2.4 0L21 14.9l-3.8 2.1-5.5 3.3c-.7.4-1.6.4-2.4 0L4.9 17.7c-.7-.4-1.2-1.2-1.2-2.1V11.5c0-.8.4-1.6 1.2-2.1l4.3-2.4c.7-.4 1.6-.4 2.4 0l4.3 2.5c.7.4 1.2 1.2 1.2 2.1v3.3l3.8-2.2v-3.4c0-.8-.4-1.6-1.2-2.1L13.1 3c-.7-.4-1.6-.4-2.4 0L2.4 7.8C1.6 8.2 1.2 9 1.2 9.9V20c0 .8.4 1.6 1.2 2.1l8.4 4.7c.7.4 1.6.4 2.4 0l5.5-3.1 3.8-2.2 5.5-3.1c.7-.4 1.6-.4 2.4 0l4.3 2.4c.7.4 1.2 1.2 1.2 2.1v4.6c0 .8-.4 1.6-1.2 2.1l-4.3 2.5c-.7.4-1.6.4-2.4 0l-4.3-2.4c-.7-.4-1.2-1.2-1.2-2.1v-3.2l-3.8 2.2v3.3c0 .8.4 1.6 1.2 2.1l8.4 4.7c.7.4 1.6.4 2.4 0l8.4-4.7c.7-.4 1.2-1.2 1.2-2.1v-9.7c0-.8-.4-1.6-1.2-2.1L29 11.6z" />
        </svg>
      </div>
    );
  }
  if (chain === "solana") {
    // Solana brand gradient with the canonical 3-bar mark.
    return (
      <div
        className="w-10 h-10 rounded-[5px] flex items-center justify-center flex-shrink-0"
        style={{ background: "linear-gradient(135deg, #9945FF, #14F195)" }}
      >
        <svg viewBox="0 0 397.7 311.7" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
          <path d="M64.6 237.9c2.4-2.4 5.7-3.8 9.2-3.8h317.4c5.8 0 8.7 7 4.6 11.1l-62.7 62.7c-2.4 2.4-5.7 3.8-9.2 3.8H6.5c-5.8 0-8.7-7-4.6-11.1l62.7-62.7zM64.6 3.8C67.1 1.4 70.4 0 73.8 0h317.4c5.8 0 8.7 7 4.6 11.1l-62.7 62.7c-2.4 2.4-5.7 3.8-9.2 3.8H6.5c-5.8 0-8.7-7-4.6-11.1L64.6 3.8zM333.1 120.1c-2.4-2.4-5.7-3.8-9.2-3.8H6.5c-5.8 0-8.7 7-4.6 11.1l62.7 62.7c2.4 2.4 5.7 3.8 9.2 3.8h317.4c5.8 0 8.7-7 4.6-11.1l-62.7-62.6z" fill="#fff" />
        </svg>
      </div>
    );
  }
  if (chain === "arbitrum") {
    // Arbitrum brand circle — HL margin lives on Arbitrum L1 + HyperCore
    // L2 sharing the same EOA, so the Hyperliquid wallet card uses
    // Arbitrum chain colors (deep blue) with the HL source badge layered
    // on top.
    return (
      <div className="w-10 h-10 rounded-[5px] bg-[#28A0F0]/10 flex items-center justify-center flex-shrink-0">
        <svg viewBox="0 0 32 32" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
          <path d="M16 0C7.16 0 0 7.16 0 16s7.16 16 16 16 16-7.16 16-16S24.84 0 16 0zM7.7 22.27l4.97-8.33 1.93 3.24-3.83 6.42a16.7 16.7 0 0 1-3.07-1.33zm6.45 2.86l3.16-5.31 6.32 10.61a15.85 15.85 0 0 1-9.48-5.3zm10.65 5.3l-9.27-15.55-1.93-3.23-3.6 6.04a15.85 15.85 0 0 1-2.95-7.92l8.13-13.65 1.93 3.24L26.95 24.9a16.62 16.62 0 0 1-2.15 5.53zM16 6.95l-1.93-3.24a16.85 16.85 0 0 1 5.36 1.13L18.4 6.95H16z" fill="#28A0F0" />
        </svg>
      </div>
    );
  }
  // Ethereum / generic EVM (the Privy login EOA is chain-agnostic but we
  // explore it on Polygonscan, so render a neutral diamond).
  return (
    <div className="w-10 h-10 rounded-[5px] bg-gray-200 dark:bg-[#0f0f12] flex items-center justify-center flex-shrink-0">
      <svg viewBox="0 0 32 32" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
        <path d="M16 2L7 17l9 5 9-5L16 2z" fill="#627EEA" />
        <path d="M16 24l-9-5 9 13 9-13-9 5z" fill="#627EEA" opacity="0.6" />
      </svg>
    </div>
  );
}

function SourceBadge({ source }: { source: NonNullable<SourceKind> }) {
  // 18×18 platform badge overlaid on the bottom-right of the chain
  // glyph — same convention as the position cards in PositionsSection,
  // so the visual language stays consistent across portfolio surfaces.
  if (source === "polymarket") {
    return (
      <div className="absolute -bottom-1 -right-1 w-[18px] h-[18px] rounded-[4px] bg-[#3B82F6] flex items-center justify-center ring-2 ring-white dark:ring-[#18181D]">
        <img src="/logos/polymarket/icon-white.svg" alt="Polymarket" className="w-3 h-3" />
      </div>
    );
  }
  if (source === "hyperliquid") {
    // HL brand mark — matches the source-pill convention in PositionsSection.
    return (
      <div className="absolute -bottom-1 -right-1 w-[18px] h-[18px] rounded-[4px] bg-[#0f1a1f] flex items-center justify-center ring-2 ring-white dark:ring-[#18181D]">
        <img src="/logos/hyperliquid/symbol-mint.svg" alt="Hyperliquid" className="w-3 h-3" />
      </div>
    );
  }
  // Kalshi — emerald with a "K" mark (matches PositionsSection badge).
  return (
    <div className="absolute -bottom-1 -right-1 w-[18px] h-[18px] rounded-[4px] bg-emerald-500 flex items-center justify-center ring-2 ring-white dark:ring-[#18181D]">
      <span className="text-white font-bold text-[9px] leading-none">K</span>
    </div>
  );
}

function WalletAvatar({ chain, source }: { chain: ChainKind; source: SourceKind }) {
  return (
    <div className="relative flex-shrink-0">
      <ChainGlyph chain={chain} />
      {source && <SourceBadge source={source} />}
    </div>
  );
}

function WalletCard({ data }: { data: WalletCardData }) {
  const t = useT();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.address);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* no-op */ }
  };

  const badgeColors: Record<NonNullable<WalletCardData["badgeTone"]>, string> = {
    active: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20",
    legacy: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20",
    auth: "bg-gray-500/10 text-gray-600 dark:text-gray-400 border border-gray-500/20",
  };

  return (
    <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors">
      <div className="flex items-center gap-3 p-4">
        <WalletAvatar chain={data.chain} source={data.source} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="font-monument text-sm text-gray-900 dark:text-white truncate">
              {data.label}
            </span>
            {data.badge && (
              <span
                className={`px-1.5 py-0.5 rounded-[3px] text-[9px] font-semibold uppercase tracking-wide ${
                  badgeColors[data.badgeTone || "auth"]
                }`}
              >
                {data.badge}
              </span>
            )}
          </div>
          {data.sublabel && (
            <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5 truncate">
              {data.sublabel}
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <a
              href={data.explorerUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] font-mono text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors flex items-center gap-1"
            >
              {truncate(data.address)}
              <FiExternalLink className="w-2.5 h-2.5 opacity-60" />
            </a>
            <button
              onClick={copy}
              className="p-0.5 hover:bg-gray-100 dark:hover:bg-white/5 rounded transition-colors"
              title={t("portfolio.profile.copyAddress")}
            >
              {copied
                ? <FiCheck className="w-3 h-3 text-emerald-500" />
                : <FiCopy className="w-3 h-3 text-gray-400" />
              }
            </button>
          </div>
        </div>
        {!data.collapsed && (
          <div className="text-right flex-shrink-0">
            <div className="font-monument text-sm text-gray-900 dark:text-white tabular-nums">
              {data.primaryLoading
                ? <span className="text-gray-400">...</span>
                : data.primaryBalance != null
                  ? formatCurrency(data.primaryBalance)
                  : <span className="text-gray-400">—</span>
              }
            </div>
            {data.primaryBalanceLabel && (
              <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                {data.primaryBalanceLabel}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Gas + positions row. We hide the gas chip when the balance
          rounds to 0 at the 4-decimal precision we display anyway:
          Polymarket V2 / pUSD / Safe relayer covers gas for ordinary
          trading, so "0.0000 POL" was visual noise that confused users
          into thinking they had a blocker. Real gas balances above
          0.0001 still render so the user can spot a topped-up wallet. */}
      {(() => {
        const hasGas = !!data.gasBalance && data.gasBalance.amount >= 0.0001;
        const hasPositions = data.positionsCount != null;
        if (data.collapsed || (!hasGas && !hasPositions)) return null;
        return (
          <div className="px-4 pb-3 flex items-center gap-3 text-[11px] text-gray-500 dark:text-gray-400">
            {hasGas && (
              <span className="tabular-nums">
                {data.gasBalance!.amount.toFixed(4)} {data.gasBalance!.symbol}
              </span>
            )}
            {hasGas && hasPositions && (
              <span className="text-gray-300 dark:text-gray-600">·</span>
            )}
            {hasPositions && (
              <span className="tabular-nums">
                {data.positionsCount} {data.positionsLabel || "open positions"}
                {data.positionsValue != null && data.positionsValue > 0 && (
                  <span className="text-gray-700 dark:text-gray-300 ml-1">
                    ({formatCurrency(data.positionsValue)})
                  </span>
                )}
              </span>
            )}
          </div>
        );
      })()}

      {data.warning && (
        // 2026-05-22 (Pika): warning was a bare amber-text line below
        // the card, read as decoration noise. Wrapped in the standard
        // amber banner pattern (matching the one used in
        // PortfolioProfileBar `(needs claim)` row and the
        // CopytradingDashboard penny-slippage warning) so the alert
        // has its own visual weight: tinted bg + thin border +
        // padded box. Still inside the card padding so it doesn't
        // expand the card width.
        <div className="mx-4 mb-3 flex items-start gap-2 rounded-[5px] border border-amber-300/60 bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-700 dark:border-amber-300/20 dark:bg-amber-400/10 dark:text-amber-300">
          <FiAlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>{data.warning}</span>
        </div>
      )}

      {data.polygonOnly && <NetworkWarningBadge className="mx-4 mb-3" />}
    </div>
  );
}

interface WalletCardsSectionProps {
  /** Optional summary numbers from the unified positions hook so the
   *  per-wallet "open positions" rows match what the rest of the page
   *  shows. The component degrades gracefully if not provided. */
  polymarketPositionsValue?: number;
  polymarketPositionsCount?: number;
  kalshiPositionsValue?: number;
  kalshiPositionsCount?: number;
  hyperliquidPositionsValue?: number;
  hyperliquidPositionsCount?: number;
}

export default function WalletCardsSection({
  polymarketPositionsValue = 0,
  polymarketPositionsCount = 0,
  kalshiPositionsValue = 0,
  kalshiPositionsCount = 0,
  hyperliquidPositionsValue = 0,
  hyperliquidPositionsCount = 0,
}: WalletCardsSectionProps) {
  const t = useT();
  const { data: userMe } = useUserMeQuery();
  const polyBalance = usePolymarketBalanceContext();
  const solana = useSolanaUSDCBalance();
  const hl = useHyperliquidBalance();

  const safeAddress: string | null = (userMe as any)?.safe_address || null;
  const depositWalletAddress: string | null = (userMe as any)?.deposit_wallet_address || null;
  const privyEoaAddress: string | null = (userMe as any)?.wallet_address || null;
  const walletType: "safe" | "deposit" | null = (userMe as any)?.wallet_type || null;

  // Lazy fetch the legacy Safe balance only when:
  //  - the user has migrated to a deposit wallet (so the Safe is "legacy")
  //  - and the address is set
  // We don't want to hit the public balance endpoint on every portfolio
  // render for users who never had a Safe.
  const shouldFetchLegacySafe = walletType === "deposit" && !!safeAddress;
  const { data: legacySafeBalance, isLoading: legacySafeLoading } = useExternalWalletBalance(
    shouldFetchLegacySafe ? safeAddress : null,
  );

  const cards = useMemo<WalletCardData[]>(() => {
    const out: WalletCardData[] = [];

    // 1. Primary Polymarket trading wallet — labelled "Deposit Wallet"
    //    on the post-migration custody model, "Safe Wallet" pre-migration.
    const tradingAddr = polyBalance.depositWalletAddress || polyBalance.safeAddress || null;
    if (tradingAddr) {
      // pUSD is wrapped USDC.e (1:1 trading collateral). Sum both for
      // "what the user can actually trade with right now" — matches
      // PolymarketBalanceContext.balance.
      const usdc = polyBalance.balance ?? 0;
      const isDeposit = walletType === "deposit";
      out.push({
        kind: isDeposit ? "deposit" : "safe-legacy",
        label: isDeposit ? t("portfolio.wallets.depositWallet") : t("portfolio.wallets.safeWallet"),
        sublabel: "Polygon · Polymarket",
        badge: t("portfolio.wallets.active"),
        badgeTone: "active",
        chain: "polygon",
        source: "polymarket",
        address: tradingAddr,
        explorerUrl: `https://polygonscan.com/address/${tradingAddr}`,
        primaryBalance: usdc,
        primaryBalanceLabel: t("portfolio.wallets.usdcTradable"),
        primaryLoading: polyBalance.loading,
        gasBalance: polyBalance.polBalance != null
          ? { amount: polyBalance.polBalance, symbol: "POL" }
          : null,
        positionsCount: polymarketPositionsCount,
        positionsValue: polymarketPositionsValue,
        positionsLabel: t("portfolio.wallets.positionsLabel", { platform: "Polymarket" }),
        polygonOnly: true,
      });
    }

    // 2. Solana — Kalshi collateral lives here as USDC SPL.
    if (solana.solanaAddress) {
      out.push({
        kind: "solana",
        label: t("portfolio.wallets.solanaWallet"),
        sublabel: "Solana · Kalshi",
        badge: t("portfolio.wallets.active"),
        badgeTone: "active",
        chain: "solana",
        source: "kalshi",
        address: solana.solanaAddress,
        explorerUrl: `https://solscan.io/account/${solana.solanaAddress}`,
        primaryBalance: solana.balance ?? 0,
        primaryBalanceLabel: t("portfolio.wallets.usdcAvailable"),
        primaryLoading: solana.loading,
        positionsCount: kalshiPositionsCount,
        positionsValue: kalshiPositionsValue,
        positionsLabel: t("portfolio.wallets.positionsLabel", { platform: "Kalshi" }),
      });
    }

    // 3. Polymarket Legacy Safe — only shown when the user has already
    //    migrated to a deposit wallet AND the Safe still holds residual
    //    funds or open V2 positions. Hidden when drained.
    if (
      shouldFetchLegacySafe &&
      safeAddress &&
      legacySafeBalance &&
      (legacySafeBalance.tradeable > 0.01 ||
        legacySafeBalance.usdc_native > 0.01 ||
        legacySafeBalance.portfolio_value > 0.01)
    ) {
      const tradable = legacySafeBalance.tradeable + legacySafeBalance.usdc_native;
      out.push({
        kind: "safe-legacy",
        label: t("portfolio.wallets.safeWallet"),
        sublabel: "Polygon · Polymarket (legacy)",
        badge: t("portfolio.wallets.legacy"),
        badgeTone: "legacy",
        chain: "polygon",
        source: "polymarket",
        address: safeAddress,
        explorerUrl: `https://polygonscan.com/address/${safeAddress}`,
        primaryBalance: tradable,
        primaryBalanceLabel: t("portfolio.wallets.residualBalance"),
        primaryLoading: legacySafeLoading,
        gasBalance: legacySafeBalance.pol > 0
          ? { amount: legacySafeBalance.pol, symbol: "POL" }
          : null,
        warning:
          legacySafeBalance.tradeable > 0.01 || legacySafeBalance.usdc_native > 0.01
            ? t("portfolio.wallets.warnFunds")
            : legacySafeBalance.portfolio_value > 0.01
              ? t("portfolio.wallets.warnPositions")
              : null,
        polygonOnly: true,
      });
    }

    // 4. Hyperliquid wallet — same Privy EVM EOA, but anchored on
    //    Arbitrum where HL margin lives. Surfaced as its own card when
    //    the user has any HL footprint (HL margin, Arbitrum-side USDC
    //    waiting to bridge, or open HL positions); collapses back into
    //    a generic Login card when the user has never touched HL.
    const hlActive =
      (hl.hlBalance ?? 0) > 0.01 ||
      (hl.arbitrumBalance ?? 0) > 0.01 ||
      hyperliquidPositionsCount > 0;
    if (hlActive && privyEoaAddress) {
      const usdcTotal = (hl.hlBalance ?? 0) + (hl.arbitrumBalance ?? 0);
      out.push({
        kind: "hyperliquid",
        label: t("portfolio.wallets.hyperliquidWallet"),
        sublabel: "Arbitrum · Hyperliquid",
        badge: t("portfolio.wallets.active"),
        badgeTone: "active",
        chain: "arbitrum",
        source: "hyperliquid",
        address: privyEoaAddress,
        explorerUrl: `https://hypurrscan.io/address/${privyEoaAddress}`,
        primaryBalance: usdcTotal,
        primaryBalanceLabel:
          (hl.hlBalance ?? 0) > 0 && (hl.arbitrumBalance ?? 0) > 0
            ? t("portfolio.wallets.usdcHlArb")
            : (hl.hlBalance ?? 0) > 0
              ? t("portfolio.wallets.usdcOnHl")
              : t("portfolio.wallets.usdcOnArb"),
        primaryLoading: hl.loading,
        positionsCount: hyperliquidPositionsCount,
        positionsValue: hyperliquidPositionsValue,
        positionsLabel: t("portfolio.wallets.positionsLabel", { platform: "HL" }),
      });
    }

    // 5. Privy login EOA — collapsed, signing-only. Skipped when:
    //    - it matches another card's address (redundant), or
    //    - the Hyperliquid card is shown (it already represents the EVM
    //      EOA in a richer form). The login card only surfaces when the
    //      user has no HL footprint and the EOA isn't already on screen.
    const knownAddrs = new Set(
      [tradingAddr, safeAddress, depositWalletAddress]
        .filter(Boolean)
        .map((a) => a!.toLowerCase()),
    );
    if (
      privyEoaAddress &&
      !knownAddrs.has(privyEoaAddress.toLowerCase()) &&
      !hlActive
    ) {
      out.push({
        kind: "evm-login",
        label: t("portfolio.wallets.evmWallet"),
        sublabel: t("portfolio.wallets.signingOnly"),
        badge: t("portfolio.wallets.auth"),
        badgeTone: "auth",
        chain: "ethereum",
        source: null,
        address: privyEoaAddress,
        explorerUrl: `https://polygonscan.com/address/${privyEoaAddress}`,
        primaryBalance: null,
        collapsed: true,
      });
    }

    return out;
  }, [
    polyBalance.depositWalletAddress,
    polyBalance.safeAddress,
    polyBalance.balance,
    polyBalance.polBalance,
    polyBalance.loading,
    walletType,
    solana.solanaAddress,
    solana.balance,
    solana.loading,
    hl.hlBalance,
    hl.arbitrumBalance,
    hl.loading,
    privyEoaAddress,
    safeAddress,
    depositWalletAddress,
    legacySafeBalance,
    legacySafeLoading,
    shouldFetchLegacySafe,
    polymarketPositionsValue,
    polymarketPositionsCount,
    kalshiPositionsValue,
    kalshiPositionsCount,
    hyperliquidPositionsValue,
    hyperliquidPositionsCount,
    t,
  ]);

  if (cards.length === 0) return null;

  return (
    // 2026-05-22 (Pika): WALLETS used to render flush against the
    // previous section with only mb-2 of breathing room — read as
    // a continuation of the card above, not its own section. Added
    // mt-8 lg:mt-10 (32-40px) so the section visually decouples,
    // matching the spacing the home uses around "TRADE ACROSS".
    // The header label gets a slightly larger top space (mb-3) +
    // [11px] uppercase tracking-wider for full consistency with
    // the rest of the section headers in the product.
    <div className="mt-8 lg:mt-10">
      <div className="flex items-center justify-between mb-3 px-1">
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          {t("portfolio.wallets.sectionTitle")}
        </h3>
        <span className="text-[10px] text-gray-400 dark:text-gray-500">
          {cards.length} {cards.length === 1 ? t("portfolio.wallets.unit") : t("portfolio.wallets.unitPlural")}
        </span>
      </div>
      <div className="space-y-2">
        {cards.map((card) => (
          <WalletCard key={`${card.kind}-${card.address}`} data={card} />
        ))}
      </div>
    </div>
  );
}
